import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

interface UserMenuProps {
  email: string | null;
  /** Whether to offer the Admin entry: the caller holds `VIEW_ADMIN`, the permission `/api/v1/admin` checks. */
  showAdmin: boolean;
  /** Whether to offer the Users entry: the caller holds `VIEW_USERS`. */
  showUsers: boolean;
  onLogout: () => void;
}

/**
 * The signed-in user's email at the right of the app bar, opening a menu with Admin and Users (each when allowed) and
 * Logout. One text across the Bevi UIs, so it names the two paths every one of them serves rather than importing a
 * route table whose shape differs per app.
 */
export function UserMenu({ email, showAdmin, showUsers, onLogout }: UserMenuProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const close = () => setAnchorEl(null);

  return (
    <>
      <Button
        color="inherit"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        endIcon={<KeyboardArrowDownIcon />}
        sx={{ textTransform: 'none' }}
        aria-haspopup="menu"
        aria-expanded={anchorEl !== null}
        data-testid="user-email"
      >
        {email ?? 'Signed in'}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={anchorEl !== null}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {showAdmin && (
          <MenuItem component={RouterLink} to="/admin" onClick={close} data-testid="nav-admin">
            Admin
          </MenuItem>
        )}
        {showUsers && (
          <MenuItem component={RouterLink} to="/users" onClick={close} data-testid="nav-users">
            Users
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            close();
            onLogout();
          }}
          data-testid="logout-button"
        >
          Logout
        </MenuItem>
      </Menu>
    </>
  );
}
